import { create, destroy } from '@dojo/framework/core/vdom';
import has from '@dojo/framework/core/has';
import { ParentTree, RecommendedTopics } from '@holmescorp/lcc-interfaces';

import { lccstate, userstate, keymap, pathmap } from '..';

export interface Recommended {
	topichead: string;
	topicset: string;
	title: string;
	basepath: string;
	percent: number;
	isactive: boolean;
	completed: boolean;
	pretest: number;
	isreadingstarted: boolean;
	isreadingcompleted: boolean;
	isquizcompleted: boolean;
	recommendedorder?: number;
}

export interface Tracked {
	recommended: Recommended[];
	allcompleted: boolean[];
	tgfkeys: string[];
	assessment: ParentTree;
}

export interface RecommendedAPI {
	tracked: Tracked;
	setup(): void;
	recalculate(): void;
	hasAssessment(): boolean;
	assessmentsComplete(): boolean;
	allcomplete(): boolean;
	topicsets(noCompleted: boolean): { attributes: Record<string, any>; children: any[] };
	assessResults(): { topichead: string; title: string; percent: number; isTestCompleted: boolean }[];
	firstIncomplete(): string | null;
}

const factory = create({ destroy, lccstate, userstate });

export const recommended = factory(({ middleware: { destroy, lccstate, userstate } }) => {
	const { get, path } = userstate;
	const handles: any[] = [];
	destroy(() => {
		let handle: any;
		while ((handle = handles.pop())) {
			handle();
		}
	});

	const api: RecommendedAPI = {
		tracked: {
			recommended: [],
			allcompleted: [],
			tgfkeys: [],
			assessment: {} as ParentTree
		},

		setup(this: RecommendedAPI): void {
			this.recalculate();
			const store = userstate.getStore();
			const { path } = store;
			const handle = store.onChange([path('status'), path('assessment')], () => {
				this.recalculate();
			});
			handles.push(() => handle.remove());
		},

		recalculate(this: RecommendedAPI): void {
			const assessment: RecommendedTopics = (this.tracked.assessment = get(path('assessment')));
			const status = get(path('status'));
			if (has('use-ml-study-plan')) {
				this.tracked.tgfkeys = Object.keys(assessment).filter(
					(k) => assessment[k].data.order !== undefined && assessment[k].data.questioncount === undefined
				);
			} else {
				this.tracked.tgfkeys = Object.keys(assessment).filter((k) => assessment[k].parent !== null);
			}
			this.tracked.allcompleted = [];

			const { tgfkeys, allcompleted } = this.tracked;

			this.tracked.recommended = tgfkeys
				.map((k): Recommended => {
					const [topichead, topicset] = [assessment[k].parent!, k];
					const basepath = `/explore/${topichead}/${topicset}`;
					const tskey = keymap.get(topicset);
					const title: string = tskey ? tskey.title : '';

					const { data, parent } = status[topicset];
					const completed = data.percent >= 0.8 && data.complete === data.total;
					const isactive = !completed && (data.complete > 0 || data.percent > 0);
					// additional metadata for use to calc card state
					const pretest = assessment[parent].data.percent;
					const isreadingstarted = data.complete > 0;
					const isreadingcompleted = data.complete === data.total;
					const isquizcompleted = data.percent > 0;
					const recommendedorder = assessment[topicset].data.order || 0;
					allcompleted.push(completed);

					const { percent } = assessment![k].data;
					return {
						topichead,
						topicset,
						title,
						basepath,
						percent,
						isactive,
						completed,
						pretest,
						isreadingstarted,
						isreadingcompleted,
						isquizcompleted,
						recommendedorder
					};
				})
				.sort((l, r) => {
					if (has('use-ml-study-plan')) {
						if (l.recommendedorder !== undefined && r.recommendedorder !== undefined) {
							return l.recommendedorder - r.recommendedorder;
						} else {
							return 0;
						}
					} else {
						if (l.percent !== r.percent) {
							return l.percent - r.percent;
						}
						return l.topicset > r.topicset ? 1 : -1;
					}
				});
		},

		hasAssessment(this: RecommendedAPI): boolean {
			return this.tracked.tgfkeys.length > 0 && this.assessResults().some((v) => v.isTestCompleted);
		},

		assessmentsComplete(this: RecommendedAPI): boolean {
			return this.tracked.tgfkeys.length > 0 && this.assessResults().every((v) => v.isTestCompleted);
		},

		allcomplete(this: RecommendedAPI): boolean {
			const { allcompleted } = this.tracked;
			return allcompleted.length > 0 && allcompleted.every((v) => v);
		},

		topicsets(this: RecommendedAPI, noCompleted: boolean): { attributes: Record<string, any>; children: any[] } {
			const { recommended } = this.tracked;
			const rlist = noCompleted ? recommended.filter((item) => !item.completed) : recommended;
			const topic: { attributes: Record<string, any>; children: any[] } = {
				attributes: { key: `content-view` },
				children: []
			};
			const topicsets = rlist.map((recitem) => {
				const { topicset } = recitem;
				const { path } = keymap.get(topicset)!;
				const { topic } = pathmap.get(path)!;
				return { ...topic };
			});
			topic.children = topicsets.flat();
			return topic;
		},

		assessResults(
			this: RecommendedAPI
		): { topichead: string; title: string; percent: number; isTestCompleted: boolean }[] {
			const roots: string[] = lccstate.get(lccstate.path('ditaroots'));
			const { assessment } = this.tracked;
			return roots.map((root) => {
				const { title = root, id = root } = keymap.get(root) || {};
				const assessmentTopic = assessment[id] ? assessment[id] : assessment[root];
				const { percent = 0, pointscorrect = 0, pointsincorrect = 0 } = assessmentTopic.data;
				const isTestCompleted = percent > 0 || pointscorrect > 0 || pointsincorrect > 0;
				return {
					topichead: root,
					title,
					percent,
					isTestCompleted
				};
			});
		},

		firstIncomplete(this: RecommendedAPI): string | null {
			const { recommended } = this.tracked;
			const incomplete = recommended.filter((item) => !item.completed);
			if (incomplete.length > 0) {
				return incomplete[0].topicset;
			}
			return null;
		}
	};

	api.setup();
	return api;
});
